import { useEffect, useState } from "react";
import { BookOpen, Download, FileText, Wallet, QrCode, Loader2, RefreshCw } from "lucide-react";
import api, { fmtINR, formatDetail } from "@/lib/api";

/**
 * Unified Statement — two tabs:
 *  - Payouts Ledger: existing wallet credit/debit ledger
 *  - PayIn (Collections): captures with per-txn commission, filterable
 *    by date (max 60d), MID, status, vendor; per-row PDF receipt.
 */
export default function PanelStatement() {
    const [tab, setTab] = useState("payin");
    return (
        <div data-testid="panel-statement" className="p-6 space-y-5 max-w-6xl mx-auto">
            <div>
                <h1 className="text-xl font-bold flex items-center gap-2">
                    <BookOpen className="w-5 h-5" /> Statement
                </h1>
                <p className="text-xs text-slate-500 mt-1">All wallet movements and PayIn captures in one place.</p>
            </div>
            <div className="flex border-b border-slate-200">
                <TabBtn id="payin" cur={tab} on={setTab} icon={QrCode} label="PayIn Collections" />
                <TabBtn id="payouts" cur={tab} on={setTab} icon={Wallet} label="Payouts Ledger" />
            </div>
            {tab === "payin" ? <PayinStatement /> : <PayoutsLedger />}
        </div>
    );
}

const TabBtn = ({ id, cur, on, icon: Icon, label }) => (
    <button data-testid={`stmt-tab-${id}`} onClick={() => on(id)}
            className={`px-4 py-2.5 text-xs font-bold inline-flex items-center gap-1.5 border-b-2 transition ${cur === id ? "border-indigo-600 text-indigo-700" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
        <Icon className="w-3.5 h-3.5" /> {label}
    </button>
);

// ───────────────────── PayIn (Collections) tab ─────────────────────
function PayinStatement() {
    const today = new Date().toISOString().slice(0, 10);
    const d7ago = new Date(Date.now() - 7 * 86400000).toISOString().slice(0, 10);
    const [from, setFrom] = useState(d7ago);
    const [to, setTo] = useState(today);
    const [midId, setMidId] = useState("");
    const [status, setStatus] = useState("");
    const [submerchant, setSubmerchant] = useState("");
    const [mids, setMids] = useState([]);
    const [subms, setSubms] = useState([]);
    const [data, setData] = useState({ items: [], totals: {}, total: 0 });
    const [page, setPage] = useState(1);
    const limit = 50;
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const [downloading, setDownloading] = useState("");

    useEffect(() => {
        (async () => {
            try {
                const [r, s] = await Promise.all([
                    api.get("/partner/payin/mids"),
                    api.get("/partner/payin/submerchants").catch(() => ({ data: { items: [] } })),
                ]);
                setMids(r.data.items || []);
                setSubms(s.data.items || []);
            } catch {}
        })();
    }, []);

    const dayDiff = (() => {
        if (!from || !to) return 0;
        return Math.ceil((new Date(to) - new Date(from)) / 86400000);
    })();
    const windowValid = dayDiff >= 0 && dayDiff <= 60;

    const load = async () => {
        if (!windowValid) { setErr("Max window is 60 days"); return; }
        setBusy(true); setErr("");
        try {
            const r = await api.get("/partner/payin/statement", {
                params: { date_from: from, date_to: to, mid_id: midId || undefined,
                              status: status || undefined,
                              submerchant: submerchant || undefined,
                              page, limit },
            });
            setData(r.data);
        } catch (e) {
            setErr(formatDetail(e?.response?.data?.detail) || e.message);
        } finally { setBusy(false); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [page]);

    const apply = () => { setPage(1); load(); };

    const exportCsv = async () => {
        setDownloading("csv");
        try {
            const r = await api.get("/partner/payin/statement/export.csv", {
                params: { date_from: from, date_to: to, mid_id: midId || undefined,
                              status: status || undefined,
                              submerchant: submerchant || undefined },
                responseType: "blob",
            });
            const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
            const a = document.createElement("a");
            a.href = url; a.download = `payin-statement-${from}_to_${to}.csv`;
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(url);
        } catch (e) { setErr(formatDetail(e?.response?.data?.detail) || e.message); }
        finally { setDownloading(""); }
    };

    const downloadReceipt = async (orderId, refId) => {
        setDownloading(orderId);
        try {
            const r = await api.get(`/partner/payin/orders/${orderId}/receipt.pdf`, { responseType: "blob" });
            const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
            const a = document.createElement("a");
            a.href = url; a.download = `receipt-${refId || orderId}.pdf`;
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(url);
        } catch (e) { setErr(formatDetail(e?.response?.data?.detail) || e.message); }
        finally { setDownloading(""); }
    };

    const totalPages = Math.max(1, Math.ceil((data.total || 0) / limit));

    return (
        <div className="space-y-4" data-testid="stmt-payin">
            {/* Filters */}
            <div className="bg-white border border-slate-200 rounded-xl p-3 flex flex-wrap gap-3 items-end">
                <div>
                    <label className="text-[11px] font-semibold text-slate-600 uppercase tracking-wider">From</label>
                    <input data-testid="stmt-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)}
                              className="input text-sm mt-1" />
                </div>
                <div>
                    <label className="text-[11px] font-semibold text-slate-600 uppercase tracking-wider">To</label>
                    <input data-testid="stmt-to" type="date" value={to} onChange={(e) => setTo(e.target.value)}
                              className="input text-sm mt-1" />
                </div>
                <div>
                    <label className="text-[11px] font-semibold text-slate-600 uppercase tracking-wider">MID</label>
                    <select data-testid="stmt-mid" value={midId} onChange={(e) => setMidId(e.target.value)}
                                className="input text-sm mt-1 min-w-[180px]">
                        <option value="">All MIDs</option>
                        {mids.map((m) => <option key={m.mid_id} value={m.mid_id}>{m.label}</option>)}
                    </select>
                </div>
                <div>
                    <label className="text-[11px] font-semibold text-slate-600 uppercase tracking-wider">Status</label>
                    <select data-testid="stmt-status" value={status} onChange={(e) => setStatus(e.target.value)}
                                className="input text-sm mt-1 min-w-[140px]">
                        <option value="">All</option>
                        <option value="CAPTURED">Captured</option>
                        <option value="AWAITING">Awaiting</option>
                        <option value="FAILED">Failed</option>
                    </select>
                </div>
                <div>
                    <label className="text-[11px] font-semibold text-slate-600 uppercase tracking-wider">Submerchant</label>
                    <select data-testid="stmt-submerchant" value={submerchant} onChange={(e) => setSubmerchant(e.target.value)}
                                className="input text-sm mt-1 min-w-[160px]"
                                disabled={subms.length === 0}>
                        <option value="">All</option>
                        {subms.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                </div>
                <button data-testid="stmt-apply" onClick={apply} disabled={busy || !windowValid}
                        className="btn-primary px-4 py-2 rounded-md text-sm font-semibold flex items-center gap-1.5 disabled:opacity-50">
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Apply
                </button>
                <div className="flex-1" />
                <button data-testid="stmt-csv" onClick={exportCsv} disabled={downloading === "csv" || data.total === 0}
                        className="h-9 px-3 inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white text-xs font-semibold text-slate-700 hover:bg-slate-50 hover:border-indigo-300 hover:text-indigo-700 disabled:opacity-50">
                    {downloading === "csv" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />} Export CSV
                </button>
            </div>

            {!windowValid && (
                <div className="bg-amber-50 border border-amber-200 rounded-md p-2.5 text-xs text-amber-900">
                    Max statement window is 60 days. Narrow the date range to continue.
                </div>
            )}
            {err && <div data-testid="stmt-err" className="bg-rose-50 border border-rose-200 rounded p-2.5 text-xs text-rose-700">{err}</div>}

            {/* Totals strip */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <TotalCard label="Txns" value={data.total || 0} mono={false} />
                <TotalCard label="Gross" value={fmtINR(data.totals?.gross || 0)} tone="slate" />
                <TotalCard label="Commission" value={fmtINR(data.totals?.commission || 0)} tone="rose" />
                <TotalCard label="Net credit" value={fmtINR(data.totals?.net || 0)} tone="emerald" />
            </div>

            {/* Table */}
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                        <thead className="bg-slate-50 border-b border-slate-200">
                            <tr>
                                <th className="px-3 py-2 text-left">Date</th>
                                <th className="px-3 py-2 text-left">Ref</th>
                                <th className="px-3 py-2 text-left">MID</th>
                                <th className="px-3 py-2 text-left">Submerchant</th>
                                <th className="px-3 py-2 text-right">Gross</th>
                                <th className="px-3 py-2 text-right">Comm %</th>
                                <th className="px-3 py-2 text-right">Comm ₹</th>
                                <th className="px-3 py-2 text-right">Net</th>
                                <th className="px-3 py-2 text-left">Status</th>
                                <th className="px-3 py-2 text-right">Receipt</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(data.items || []).length === 0 ? (
                                <tr><td colSpan={10} className="px-3 py-10 text-center text-slate-400">No PayIn transactions in this range.</td></tr>
                            ) : (data.items || []).map((o) => (
                                <tr key={o.order_id} data-testid={`stmt-row-${o.order_id}`} className="border-b border-slate-50 hover:bg-slate-50/50">
                                    <td className="px-3 py-2 font-mono text-[10px] text-slate-500">{new Date(o.created_at).toLocaleString()}</td>
                                    <td className="px-3 py-2 font-mono text-[10px]" title={o.order_id}>{o.client_reference_id || o.order_id}</td>
                                    <td className="px-3 py-2 text-[11px]">{o.mid_label || o.mid_id}</td>
                                    <td className="px-3 py-2 text-[11px]">
                                        {o.submerchant_label ? (
                                            <span className="px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 text-[10px] font-semibold">{o.submerchant_label}</span>
                                        ) : <span className="text-slate-300">—</span>}
                                    </td>
                                    <td className="px-3 py-2 text-right font-mono font-semibold text-slate-900">{fmtINR(o.amount)}</td>
                                    <td className="px-3 py-2 text-right font-mono text-slate-600">{o.commission_percent ? `${o.commission_percent}%` : "—"}</td>
                                    <td className="px-3 py-2 text-right font-mono text-rose-700">{o.commission_amount ? fmtINR(o.commission_amount) : "—"}</td>
                                    <td className="px-3 py-2 text-right font-mono font-semibold text-emerald-700">{o.net_amount != null ? fmtINR(o.net_amount) : fmtINR(o.amount)}</td>
                                    <td className="px-3 py-2">
                                        <StatusBadge s={o.status} />
                                    </td>
                                    <td className="px-3 py-2 text-right">
                                        {o.status === "CAPTURED" ? (
                                            <button data-testid={`stmt-receipt-${o.order_id}`} onClick={() => downloadReceipt(o.order_id, o.client_reference_id)}
                                                        disabled={downloading === o.order_id}
                                                        title="Download PDF receipt"
                                                        className="inline-flex items-center gap-1 text-[11px] text-indigo-600 hover:text-indigo-800 font-semibold disabled:opacity-50">
                                                {downloading === o.order_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileText className="w-3 h-3" />} PDF
                                            </button>
                                        ) : <span className="text-slate-300">—</span>}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                {totalPages > 1 && (
                    <div className="flex items-center justify-between px-3 py-2 bg-slate-50 border-t border-slate-200 text-xs">
                        <span className="text-slate-500">Page {page} / {totalPages} · {data.total} txns</span>
                        <div className="flex gap-2">
                            <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}
                                    className="px-2 py-1 rounded border border-slate-200 disabled:opacity-40 hover:bg-white">Prev</button>
                            <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                                    className="px-2 py-1 rounded border border-slate-200 disabled:opacity-40 hover:bg-white">Next</button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

const TOTAL_TONES = {
    slate: "text-slate-900",
    rose: "text-rose-700",
    emerald: "text-emerald-700",
};
const TotalCard = ({ label, value, tone = "slate" }) => (
    <div className="bg-white border border-slate-200 rounded-xl p-3">
        <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
        <div className={`font-bold font-mono text-base mt-1 ${TOTAL_TONES[tone]}`}>{value}</div>
    </div>
);

const StatusBadge = ({ s }) => {
    const map = {
        CAPTURED: "bg-emerald-100 text-emerald-800",
        AWAITING: "bg-amber-100 text-amber-800",
        FAILED: "bg-rose-100 text-rose-800",
    };
    return <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${map[s] || "bg-slate-100 text-slate-700"}`}>{s}</span>;
};

// ───────────────────── Payouts Ledger tab (existing) ─────────────────────
function PayoutsLedger() {
    const [ledger, setLedger] = useState([]);
    const [err, setErr] = useState("");
    const [from, setFrom] = useState("");
    const [to, setTo] = useState("");

    useEffect(() => {
        (async () => {
            try { const r = await api.get("/partner/payouts/ledger?limit=500"); setLedger(r.data.items || []); }
            catch (e) { setErr(formatDetail(e?.response?.data?.detail) || e.message); }
        })();
    }, []);

    const filtered = ledger.filter((e) => {
        if (from && new Date(e.created_at) < new Date(from)) return false;
        if (to && new Date(e.created_at) > new Date(to + "T23:59:59")) return false;
        return true;
    });

    const totals = filtered.reduce((acc, e) => {
        if (e.direction === "credit") acc.credits += e.amount;
        else acc.debits += e.amount;
        return acc;
    }, { credits: 0, debits: 0 });

    const exportCsv = () => {
        const rows = [["Date", "Direction", "Reason", "Amount", "Reference", "Note"]];
        for (const e of filtered) {
            rows.push([
                new Date(e.created_at).toISOString(),
                e.direction, e.reason, e.amount, e.ref_id || "", e.note || "",
            ]);
        }
        const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
        const blob = new Blob([csv], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = `payouts-ledger-${new Date().toISOString().slice(0, 10)}.csv`;
        a.click(); URL.revokeObjectURL(url);
    };

    return (
        <div data-testid="stmt-payouts" className="space-y-4">
            {err && <div className="bg-rose-50 border border-rose-200 rounded p-2 text-xs text-rose-700">{err}</div>}
            <div className="bg-white border border-slate-200 rounded-xl p-3 flex flex-wrap gap-3 items-end">
                <div>
                    <label className="text-[11px] font-semibold text-slate-600 uppercase tracking-wider">From</label>
                    <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="input text-sm mt-1" />
                </div>
                <div>
                    <label className="text-[11px] font-semibold text-slate-600 uppercase tracking-wider">To</label>
                    <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="input text-sm mt-1" />
                </div>
                <div className="ml-auto flex gap-4 items-end">
                    <div className="text-right">
                        <div className="text-[10px] uppercase text-slate-500 tracking-wider">Credits</div>
                        <div className="font-bold text-emerald-700 font-mono">{fmtINR(totals.credits)}</div>
                    </div>
                    <div className="text-right">
                        <div className="text-[10px] uppercase text-slate-500 tracking-wider">Debits</div>
                        <div className="font-bold text-rose-700 font-mono">{fmtINR(totals.debits)}</div>
                    </div>
                    <button data-testid="statement-export" onClick={exportCsv} disabled={filtered.length === 0}
                            className="h-9 px-3 inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white text-xs font-semibold text-slate-700 hover:bg-slate-50 hover:border-indigo-300 hover:text-indigo-700 disabled:opacity-50">
                        <Download className="w-3.5 h-3.5" /> Export CSV
                    </button>
                </div>
            </div>
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                        <thead className="bg-slate-50 border-b border-slate-200">
                            <tr>
                                <th className="px-3 py-2 text-left">Date</th>
                                <th className="px-3 py-2 text-left">Type</th>
                                <th className="px-3 py-2 text-left">Reason</th>
                                <th className="px-3 py-2 text-right">Amount</th>
                                <th className="px-3 py-2 text-left">Reference</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.length === 0 ? (
                                <tr><td colSpan={5} className="px-3 py-10 text-center text-slate-400">No ledger entries in this range.</td></tr>
                            ) : filtered.map((e) => (
                                <tr key={e.entry_id} data-testid={`stmt-row-${e.entry_id}`} className="border-b border-slate-50">
                                    <td className="px-3 py-2 font-mono text-[10px] text-slate-500">{new Date(e.created_at).toLocaleString()}</td>
                                    <td className="px-3 py-2">
                                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${e.direction === "credit" ? "bg-emerald-100 text-emerald-800" : "bg-rose-100 text-rose-800"}`}>
                                            {e.direction.toUpperCase()}
                                        </span>
                                    </td>
                                    <td className="px-3 py-2">{e.reason}{e.note ? ` · ${e.note}` : ""}</td>
                                    <td className={`px-3 py-2 text-right font-mono font-semibold ${e.direction === "credit" ? "text-emerald-700" : "text-rose-700"}`}>
                                        {e.direction === "credit" ? "+" : "-"}{fmtINR(e.amount)}
                                    </td>
                                    <td className="px-3 py-2 font-mono text-[10px] text-slate-500">{e.ref_id || "—"}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
